// ---------------------------------------------------------------------------
// Navigation Bar Renderer
// ---------------------------------------------------------------------------

import { registerRenderer } from "../registry.js";
import type { RenderContext } from "../types.js";
import { attr, text, url } from "../escape.js";

interface NavLink {
  label: string;
  url: string;
  children?: NavLink[];
}

interface NavigationBarProps {
  logoUrl?: string;
  links?: NavLink[];
  ctaText?: string;
  ctaUrl?: string;
  sticky?: boolean;
}

function renderNavLinks(links: NavLink[]): string {
  return links
    .map((link) => {
      const childrenHtml =
        link.children && link.children.length > 0
          ? `\n          <ul class="ease-nav-submenu">\n${link.children.map((child) => `            <li><a href="${url(child.url)}">${text(child.label)}</a></li>`).join("\n")}\n          </ul>`
          : "";

      return `        <li class="ease-nav-item${link.children && link.children.length > 0 ? " ease-nav-item--has-children" : ""}">
          <a href="${url(link.url)}">${text(link.label)}</a>${childrenHtml}
        </li>`;
    })
    .join("\n");
}

function render(props: Record<string, unknown>, _ctx: RenderContext): string {
  const p = props as unknown as NavigationBarProps;

  const links = Array.isArray(p.links) ? p.links : [];
  const sticky = p.sticky !== false;
  const stickyStyle = sticky ? " position: sticky; top: 0; z-index: 1000;" : "";
  const transitionStyle = " view-transition-name: ease-site-header;";

  const logoHtml = p.logoUrl
    ? `<a href="/" class="ease-nav-logo"><img src="${url(p.logoUrl)}" alt="Logo" loading="lazy"></a>`
    : "";

  const linksHtml =
    links.length > 0
      ? `\n      <ul class="ease-nav-links">\n${renderNavLinks(links)}\n      </ul>`
      : "";

  const ctaHtml =
    p.ctaText && p.ctaUrl
      ? `\n      <a href="${url(p.ctaUrl)}" class="ease-btn ease-btn-primary ease-nav-cta">${text(p.ctaText)}</a>`
      : "";

  const script = `<script>(function(){var btn=document.querySelector('.ease-nav-toggle');var menu=document.querySelector('.ease-nav-menu');if(!btn||!menu)return;btn.addEventListener('click',function(){var expanded=btn.getAttribute('aria-expanded')==='true';btn.setAttribute('aria-expanded',String(!expanded));menu.setAttribute('aria-hidden',String(expanded));});})()</script>`;

  return `<nav class="ease-nav" aria-label="Main navigation" style="${attr(`${stickyStyle}${transitionStyle}`.trim())}">
  <div class="ease-nav-container">
    ${logoHtml}
    <button type="button" class="ease-nav-toggle" aria-label="Toggle menu" aria-expanded="false" aria-controls="ease-nav-menu">
      <span class="ease-nav-toggle-icon" aria-hidden="true">&#9776;</span>
    </button>
    <div id="ease-nav-menu" class="ease-nav-menu" aria-hidden="true">${linksHtml}${ctaHtml}
    </div>
  </div>
</nav>
${script}`;
}

registerRenderer("navigation-bar", { render });
